import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { color as styledColor, space, variant } from 'styled-system';
import { themeGet } from '@styled-system/theme-get';
import { useTranslation } from 'react-i18next';

import { htmlSanitizer } from '../../lib/helpers';
import { BaseInput } from './Input';
import Text from '../Text';

const TextInput = forwardRef(
  (
    {
      onChange,
      onBlur,
      onClick,
      value,
      error,
      label,
      labelVariant = 'body2',
      disabledVariant = 'base',
      placeholder,
      name,
      color = 'black.80',
      dataTip,
      dataOffset,
      charactersLimit,
      charactersLimitColor = 'black.80',
      charactersLimitVariant = 'small6',
      as = 'input',
      disabled,
      onKeyUp,
      type = 'text',
      m,
      warning,
      autoComplete = 'off',
      height = '94px',
      hasIcon = false,
      extraElements,
    },
    ref,
  ) => {
    const { t } = useTranslation('shared');

    const handleChange = e => {
      if (!charactersLimit) {
        onChange(e);
        return;
      }

      const textLength = e.target.value.length;

      if (textLength <= charactersLimit) {
        onChange(e);
      }
    };

    return (
      <InputWrapper ref={ref} m={m}>
        <LabelWrapper>
          <InfoWrapper>
            <Text
              variant={labelVariant}
              as="label"
              color={color}
              htmlFor={name}
              m="0"
            >
              {label}
            </Text>
            {dataTip && (
              <InfoIcon
                data-testid="tip"
                data-tip={dataTip}
                data-offset={dataOffset}
              />
            )}
          </InfoWrapper>
          {charactersLimit && (
            <Text variant={charactersLimitVariant} color={charactersLimitColor}>
              {charactersLimit - value.length} {t('charactersLeft')}
            </Text>
          )}
        </LabelWrapper>
        <Input
          data-testid="shared-text-input"
          autoComplete={autoComplete}
          id={name}
          name={name}
          onChange={handleChange}
          onBlur={onBlur}
          value={value}
          type={type}
          placeholder={placeholder}
          error={!!error}
          onKeyUp={onKeyUp}
          as={as}
          disabled={disabled}
          variant={disabledVariant}
          height={height}
          onClick={onClick}
          hasIcon={hasIcon}
        />
        {error && (
          <Message
            dangerouslySetInnerHTML={htmlSanitizer(error)}
            color="error.default"
          />
        )}
        {warning && <Message color="warning.darker">{warning}</Message>}
        {extraElements}
      </InputWrapper>
    );
  },
);

TextInput.displayName = 'TextInput';

TextInput.propTypes = {
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  value: PropTypes.string,
  error: PropTypes.string,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  name: PropTypes.string,
  color: PropTypes.string,
  dataTip: PropTypes.string,
  dataOffset: PropTypes.string,
  onKeyUp: PropTypes.func,
  onClick: PropTypes.func,
  charactersLimit: PropTypes.number,
  as: PropTypes.string,
  disabled: PropTypes.bool,
  type: PropTypes.string,
  m: PropTypes.string,
  warning: PropTypes.string,
  labelVariant: PropTypes.string,
  charactersLimitVariant: PropTypes.string,
  disabledVariant: PropTypes.string,
  autoComplete: PropTypes.string,
  height: PropTypes.string,
  hasIcon: PropTypes.bool,
  charactersLimitColor: PropTypes.string,
  extraElements: PropTypes.node,
};

export default TextInput;

const InputWrapper = styled.div`
  font-family: 'Montserrat', sans-serif;
  position: relative;
  width: 100%;
  ${space};
`;

const Input = styled(BaseInput)`
  ${props =>
    props.error &&
    `border-color: ${themeGet('colors.error.default')(props)} !important;`}

  ${props => props.as === 'textarea' && `height: ${props.height};`}
  ${props =>
    props.as === 'textarea' &&
    props.hasIcon &&
    'padding-right: 60px !important;'}

  ${variant({
    variants: {
      base: {
        '&:disabled': {
          backgroundColor: '#F9F9FA !important',
        },
      },
      whiteBg: {
        '&:disabled': {
          backgroundColor: 'white !important',
          color: '#A0A4A8 !important',
        },
      },
    },
  })}
  @media (max-width: 600px) {
    font-size: 16px !important;
  }
`;

const LabelWrapper = styled.div`
  display: flex;
  align-items: center;
  margin: 0 0 8px;
  justify-content: space-between;
`;

const InfoWrapper = styled(LabelWrapper)`
  justify-content: left;
  margin: 0;
`;

const Message = styled.span`
  ${styledColor};
  position: absolute;
  font-weight: 400;
  right: 0;
  top: calc(100% + 4px);
  font-size: 12px;
`;

const InfoIcon = styled.i`
  margin: 0 0 0 4px;
`;

InfoIcon.defaultProps = {
  className: 'fa-regular fa-circle-info fa-xs',
};
